// pages/tools/ProfitCalculator.jsx
import { useState } from 'react';

export default function ProfitCalculator() {
  const [revenue, setRevenue] = useState('');
  const [cogs, setCogs] = useState('');
  const [adSpend, setAdSpend] = useState('');
  const [shipping, setShipping] = useState('');
  const [packaging, setPackaging] = useState('');
  const [platform, setPlatform] = useState('');
  const [otherExpenses, setOtherExpenses] = useState('');
  const [results, setResults] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const calculateProfit = () => {
    if (!revenue || !cogs || !adSpend) {
      alert('Please fill in required fields: Revenue, COGS, and Ad Spend');
      return;
    }

    const totalRevenue = parseFloat(revenue);
    const totalCogs = parseFloat(cogs);
    const totalAdSpend = parseFloat(adSpend);
    const totalShipping = parseFloat(shipping) || 0;
    const totalPackaging = parseFloat(packaging) || 0;
    const totalPlatform = parseFloat(platform) || 0;
    const totalOther = parseFloat(otherExpenses) || 0;

    // Calculations
    const grossProfit = totalRevenue - totalCogs;
    const totalOperatingExpenses = totalShipping + totalPackaging + totalPlatform + totalOther;
    const totalExpenses = totalAdSpend + totalOperatingExpenses;
    const netProfit = grossProfit - totalExpenses;
    const profitMargin = ((netProfit / totalRevenue) * 100).toFixed(2);
    const grossMargin = ((grossProfit / totalRevenue) * 100).toFixed(2);
    const roas = (totalRevenue / totalAdSpend).toFixed(2);
    const roi = ((netProfit / totalExpenses) * 100).toFixed(2);
    const breakEven = totalCogs + totalExpenses;
    const cac = totalAdSpend > 0 ? (totalAdSpend / Math.max(1, totalRevenue / 1000)).toFixed(2) : '0';
    
    // Expense breakdown percentages
    const cogsPercent = ((totalCogs / totalRevenue) * 100).toFixed(1);
    const adSpendPercent = ((totalAdSpend / totalRevenue) * 100).toFixed(1);
    const operatingPercent = ((totalOperatingExpenses / totalRevenue) * 100).toFixed(1);

    setResults({
      grossProfit: grossProfit.toFixed(2),
      netProfit: netProfit.toFixed(2),
      profitMargin,
      grossMargin,
      roas,
      roi,
      breakEven: breakEven.toFixed(2),
      cac,
      totalExpenses: totalExpenses.toFixed(2),
      totalOperatingExpenses: totalOperatingExpenses.toFixed(2),
      cogsPercent,
      adSpendPercent,
      operatingPercent
    });
  };

  const resetCalculator = () => {
    setRevenue('');
    setCogs('');
    setAdSpend('');
    setShipping('');
    setPackaging('');
    setPlatform('');
    setOtherExpenses('');
    setResults(null);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(num);
  };

  const getHealthStatus = (margin) => {
    const m = parseFloat(margin);
    if (m >= 30) return { status: 'Excellent', color: 'text-green-400', bgColor: 'bg-green-400/20', borderColor: 'border-green-400/30' };
    if (m >= 20) return { status: 'Very Good', color: 'text-blue-400', bgColor: 'bg-blue-400/20', borderColor: 'border-blue-400/30' };
    if (m >= 10) return { status: 'Good', color: 'text-yellow-400', bgColor: 'bg-yellow-400/20', borderColor: 'border-yellow-400/30' };
    if (m >= 0) return { status: 'Fair', color: 'text-orange-400', bgColor: 'bg-orange-400/20', borderColor: 'border-orange-400/30' };
    return { status: 'Critical', color: 'text-red-400', bgColor: 'bg-red-400/20', borderColor: 'border-red-400/30' };
  };

  return (
    <div className="min-h-screen bg-black pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-2xl mb-6 shadow-2xl shadow-[#00809D]/50">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4">
            Advanced Profit Calculator
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
            Calculate comprehensive profitability metrics with detailed expense breakdown and insights
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Input Form */}
          <div className="lg:col-span-3 bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-[#00809D] to-[#00a0c4] rounded-full"></span>
              Enter Your Data
            </h2>

            <div className="space-y-5">
              {/* Revenue */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Total Revenue
                  <span className="text-red-400">*</span>
                  <span className="text-xs text-gray-500">(Required)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-bold">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={revenue}
                    onChange={(e) => setRevenue(e.target.value)}
                    placeholder="2,00,000"
                    className="w-full bg-black/50 border border-white/20 rounded-xl pl-10 pr-4 py-3 sm:py-4 text-white text-base sm:text-lg placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-gray-500 text-xs sm:text-sm mt-1.5">Total sales revenue generated</p>
              </div>

              {/* COGS */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Cost of Goods Sold (COGS)
                  <span className="text-red-400">*</span>
                  <span className="text-xs text-gray-500">(Required)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-bold">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={cogs}
                    onChange={(e) => setCogs(e.target.value)}
                    placeholder="80,000"
                    className="w-full bg-black/50 border border-white/20 rounded-xl pl-10 pr-4 py-3 sm:py-4 text-white text-base sm:text-lg placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-gray-500 text-xs sm:text-sm mt-1.5">Direct costs to produce/purchase products</p>
              </div>

              {/* Ad Spend */}
              <div>
                <label className="block text-white font-semibold mb-2 text-sm sm:text-base flex items-center gap-2">
                  Advertising Spend
                  <span className="text-red-400">*</span>
                  <span className="text-xs text-gray-500">(Required)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-bold">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={adSpend}
                    onChange={(e) => setAdSpend(e.target.value)}
                    placeholder="40,000"
                    className="w-full bg-black/50 border border-white/20 rounded-xl pl-10 pr-4 py-3 sm:py-4 text-white text-base sm:text-lg placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-gray-500 text-xs sm:text-sm mt-1.5">Total advertising and marketing costs</p>
              </div>

              {/* Advanced Expenses Toggle */}
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full bg-gray-800/50 border border-white/10 rounded-xl px-4 py-3 text-white font-medium hover:border-[#00809D]/50 transition-all flex items-center justify-between group"
              >
                <span className="text-sm sm:text-base">Additional Expenses (Optional)</span>
                <svg
                  className={`w-5 h-5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Advanced Expenses */}
              {showAdvanced && (
                <div className="space-y-4 bg-black/30 border border-white/10 rounded-2xl p-5 animate-in fade-in duration-300">
                  {/* Shipping */}
                  <div>
                    <label className="block text-white font-semibold mb-2 text-sm">
                      Shipping & Fulfillment
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                        ₹
                      </span>
                      <input
                        type="number"
                        value={shipping}
                        onChange={(e) => setShipping(e.target.value)}
                        placeholder="5,000"
                        className="w-full bg-black/50 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Packaging */}
                  <div>
                    <label className="block text-white font-semibold mb-2 text-sm">
                      Packaging & Materials
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                        ₹
                      </span>
                      <input
                        type="number"
                        value={packaging}
                        onChange={(e) => setPackaging(e.target.value)}
                        placeholder="3,000"
                        className="w-full bg-black/50 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Platform Fees */}
                  <div>
                    <label className="block text-white font-semibold mb-2 text-sm">
                      Platform/Payment Fees
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                        ₹
                      </span>
                      <input
                        type="number"
                        value={platform}
                        onChange={(e) => setPlatform(e.target.value)}
                        placeholder="4,000"
                        className="w-full bg-black/50 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Other */}
                  <div>
                    <label className="block text-white font-semibold mb-2 text-sm">
                      Other Operating Expenses
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                        ₹
                      </span>
                      <input
                        type="number"
                        value={otherExpenses}
                        onChange={(e) => setOtherExpenses(e.target.value)}
                        placeholder="2,000"
                        className="w-full bg-black/50 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button
                onClick={calculateProfit}
                className="flex-1 bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-6 py-3 sm:py-4 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg shadow-[#00809D]/50 text-base sm:text-lg flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Calculate Profit
              </button>
              <button
                onClick={resetCalculator}
                className="sm:w-auto bg-gray-800 text-white px-6 py-3 sm:py-4 rounded-xl font-semibold hover:bg-gray-700 transition-all"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2 space-y-6">
            {results ? (
              <>
                {/* Net Profit Card */}
                <div className={`${getHealthStatus(results.profitMargin).bgColor} border ${getHealthStatus(results.profitMargin).borderColor} rounded-3xl p-6 sm:p-8 shadow-2xl`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-gray-400 text-sm sm:text-base">Net Profit</p>
                    <span className={`px-3 py-1 ${getHealthStatus(results.profitMargin).bgColor} ${getHealthStatus(results.profitMargin).color} rounded-full text-xs font-bold`}>
                      {getHealthStatus(results.profitMargin).status}
                    </span>
                  </div>
                  <p className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-2 ${parseFloat(results.netProfit) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {parseFloat(results.netProfit) >= 0 ? '+' : ''}₹{formatNumber(Math.abs(results.netProfit))}
                  </p>
                  <p className="text-gray-300 text-sm">
                    {parseFloat(results.netProfit) >= 0 ? '✓ Profitable Campaign' : '✗ Operating at Loss'}
                  </p>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4 sm:p-5 hover:border-[#00809D]/50 transition-all">
                    <p className="text-gray-400 text-xs sm:text-sm mb-1">Profit Margin</p>
                    <p className={`text-2xl sm:text-3xl font-bold ${getHealthStatus(results.profitMargin).color}`}>
                      {results.profitMargin}%
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4 sm:p-5 hover:border-[#00809D]/50 transition-all">
                    <p className="text-gray-400 text-xs sm:text-sm mb-1">ROAS</p>
                    <p className="text-2xl sm:text-3xl font-bold text-[#00809D]">
                      {results.roas}:1
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4 sm:p-5 hover:border-[#00809D]/50 transition-all">
                    <p className="text-gray-400 text-xs sm:text-sm mb-1">ROI</p>
                    <p className="text-2xl sm:text-3xl font-bold text-purple-400">
                      {results.roi}%
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-4 sm:p-5 hover:border-[#00809D]/50 transition-all">
                    <p className="text-gray-400 text-xs sm:text-sm mb-1">Gross Margin</p>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-400">
                      {results.grossMargin}%
                    </p>
                  </div>
                </div>

                {/* Detailed Metrics */}
                <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-5 sm:p-6">
                  <h4 className="text-white font-bold mb-4 text-sm sm:text-base">Financial Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between pb-3 border-b border-white/10">
                      <span className="text-gray-400 text-xs sm:text-sm">Gross Profit</span>
                      <span className="text-white font-bold text-sm sm:text-base">₹{formatNumber(results.grossProfit)}</span>
                    </div>
                    <div className="flex items-center justify-between pb-3 border-b border-white/10">
                      <span className="text-gray-400 text-xs sm:text-sm">Total Expenses</span>
                      <span className="text-white font-bold text-sm sm:text-base">₹{formatNumber(results.totalExpenses)}</span>
                    </div>
                    <div className="flex items-center justify-between pb-3 border-b border-white/10">
                      <span className="text-gray-400 text-xs sm:text-sm">Break-Even Point</span>
                      <span className="text-white font-bold text-sm sm:text-base">₹{formatNumber(results.breakEven)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs sm:text-sm">Customer Acquisition Cost</span>
                      <span className="text-white font-bold text-sm sm:text-base">₹{formatNumber(results.cac)}</span>
                    </div>
                  </div>
                </div>

                {/* Expense Distribution */}
                <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-5 sm:p-6">
                  <h4 className="text-white font-bold mb-4 text-sm sm:text-base">Expense Distribution</h4>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-xs sm:text-sm">COGS</span>
                        <span className="text-white font-semibold text-xs sm:text-sm">{results.cogsPercent}%</span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-red-500 to-red-400 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${results.cogsPercent}%` }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-xs sm:text-sm">Ad Spend</span>
                        <span className="text-white font-semibold text-xs sm:text-sm">{results.adSpendPercent}%</span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] h-2 rounded-full transition-all duration-500"
                          style={{ width: `${results.adSpendPercent}%` }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-xs sm:text-sm">Operating Costs</span>
                        <span className="text-white font-semibold text-xs sm:text-sm">{results.operatingPercent}%</span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-yellow-500 to-yellow-400 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${results.operatingPercent}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Insights */}
                <div className="bg-gradient-to-br from-[#00809D]/10 to-[#00a0c4]/10 border border-[#00809D]/30 rounded-2xl p-5 sm:p-6">
                  <h4 className="text-white font-bold mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <svg className="w-5 h-5 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    AI-Powered Insights
                  </h4>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                    {parseFloat(results.profitMargin) >= 30 ?
                      "🎉 Exceptional profitability! Your business model is highly efficient. Focus on scaling operations while maintaining these margins. Consider reinvesting profits into growth initiatives." :
                      parseFloat(results.profitMargin) >= 20 ?
                      "✅ Very strong margins! Your business is well-positioned for growth. Identify opportunities to scale top-performing products/services." :
                      parseFloat(results.profitMargin) >= 10 ?
                      "👍 Healthy profit margins. Look for opportunities to reduce costs or increase prices to improve profitability further. Analyze your highest-margin products." :
                      parseFloat(results.profitMargin) >= 0 ?
                      "⚠️ Thin margins - careful optimization needed. Review pricing strategy, negotiate better rates with suppliers, and reduce unnecessary expenses. Focus on increasing average order value." :
                      "🚨 Critical: Operating at a loss. Immediate action required: (1) Reduce ad spend or improve targeting efficiency (2) Renegotiate supplier costs (3) Increase prices (4) Cut non-essential expenses. Consider pivoting strategy."
                    }
                  </p>
                </div>

                {/* Recommended Actions */}
                <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-5 sm:p-6">
                  <h4 className="text-white font-bold mb-4 text-sm sm:text-base">Recommended Actions</h4>
                  <ul className="space-y-3 text-xs sm:text-sm">
                    {parseFloat(results.profitMargin) >= 20 ? (
                      <>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-green-400 mt-0.5 flex-shrink-0">✓</span>
                          <span>Scale winning campaigns by 30-50%</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-green-400 mt-0.5 flex-shrink-0">✓</span>
                          <span>Test new product lines or markets</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-green-400 mt-0.5 flex-shrink-0">✓</span>
                          <span>Invest in brand building and customer retention</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-green-400 mt-0.5 flex-shrink-0">✓</span>
                          <span>Explore premium pricing tiers</span>
                        </li>
                      </>
                    ) : parseFloat(results.profitMargin) >= 10 ? (
                      <>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-yellow-400 mt-0.5 flex-shrink-0">→</span>
                          <span>Optimize ad targeting to reduce CAC</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-yellow-400 mt-0.5 flex-shrink-0">→</span>
                          <span>Negotiate better rates with suppliers (target 10-15% reduction)</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-yellow-400 mt-0.5 flex-shrink-0">→</span>
                          <span>Implement upselling and cross-selling strategies</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-yellow-400 mt-0.5 flex-shrink-0">→</span>
                          <span>Focus on high-margin products</span>
                        </li>
                      </>
                    ) : (
                      <>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-red-400 mt-0.5 flex-shrink-0">!</span>
                          <span>Pause or reduce ad spend on unprofitable campaigns immediately</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-red-400 mt-0.5 flex-shrink-0">!</span>
                          <span>Review and reduce COGS - find cheaper suppliers or increase order volumes</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-red-400 mt-0.5 flex-shrink-0">!</span>
                          <span>Increase prices by 15-25% to test market sensitivity</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300">
                          <span className="text-red-400 mt-0.5 flex-shrink-0">!</span>
                          <span>Eliminate all non-essential operating expenses</span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </>
            ) : (
              <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-8 flex flex-col items-center justify-center min-h-[500px] text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#00809D]/20 to-[#00a0c4]/20 rounded-2xl flex items-center justify-center mb-6">
                  <svg className="w-10 h-10 text-[#00809D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-gray-400 text-base sm:text-lg mb-2">Enter your data to calculate profit</p>
                <p className="text-gray-600 text-xs sm:text-sm">Fill in the required fields and click Calculate Profit</p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">Understanding Your Metrics</h3>
            <div className="space-y-4 text-sm sm:text-base">
              <div>
                <p className="font-semibold text-white mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#00809D] rounded-full"></span>
                  Gross Profit
                </p>
                <p className="text-gray-400 text-xs sm:text-sm">Revenue minus Cost of Goods Sold (COGS). Shows profit before operating expenses.</p>
              </div>
              <div>
                <p className="font-semibold text-white mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#00809D] rounded-full"></span>
                  Net Profit
                </p>
                <p className="text-gray-400 text-xs sm:text-sm">Final profit after all expenses including ad spend. The actual money you keep.</p>
              </div>
              <div>
                <p className="font-semibold text-white mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#00809D] rounded-full"></span>
                  Profit Margin
                </p>
                <p className="text-gray-400 text-xs sm:text-sm">Net profit as a percentage of revenue. Industry average is 10-20%.</p>
              </div>
              <div>
                <p className="font-semibold text-white mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#00809D] rounded-full"></span>
                  ROAS
                </p>
                <p className="text-gray-400 text-xs sm:text-sm">Return on Ad Spend. Shows how much revenue you generate per rupee spent on ads.</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">Profit Margin Benchmarks</h3>
            <div className="space-y-3">
              <div className="bg-green-400/10 border border-green-400/30 rounded-xl p-4">
                <p className="text-green-400 font-bold mb-1 text-sm sm:text-base">30%+ Excellent</p>
                <p className="text-gray-400 text-xs sm:text-sm">Best-in-class profitability. Scale aggressively.</p>
              </div>
              <div className="bg-blue-400/10 border border-blue-400/30 rounded-xl p-4">
                <p className="text-blue-400 font-bold mb-1 text-sm sm:text-base">20-30% Very Good</p>
                <p className="text-gray-400 text-xs sm:text-sm">Strong margins. Continue optimizing and growing.</p>
              </div>
              <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-xl p-4">
                <p className="text-yellow-400 font-bold mb-1 text-sm sm:text-base">10-20% Good</p>
                <p className="text-gray-400 text-xs sm:text-sm">Healthy business. Room for improvement.</p>
              </div>
              <div className="bg-orange-400/10 border border-orange-400/30 rounded-xl p-4">
                <p className="text-orange-400 font-bold mb-1 text-sm sm:text-base">0-10% Fair</p>
                <p className="text-gray-400 text-xs sm:text-sm">Thin margins. Focus on cost reduction.</p>
              </div>
              <div className="bg-red-400/10 border border-red-400/30 rounded-xl p-4">
                <p className="text-red-400 font-bold mb-1 text-sm sm:text-base">Below 0% Critical</p>
                <p className="text-gray-400 text-xs sm:text-sm">Losing money. Urgent action required.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
